/* eslint-disable eag/no-css-property */
import {zodResolver} from '@hookform/resolvers/zod';
import i18n from '@online-offer/i18n';
import throttle from 'lodash/throttle';
import {Box, getCssSize, HStack, Icon, Show, Spinner, Text, VStack} from 'platform/foundation';
import {css} from 'styled-components';
import {z} from 'zod';

import {useEffect, useMemo, useState} from 'react';
import {useForm, FormProvider, useWatch} from 'react-hook-form';

import {sendEvent, Type} from '@chat/shared/graphql';
import {sendEventAsPublicUserBody} from '@chat/shared/validations';
import {RichContentInput} from '@chat/ui';

import {chatPublicApi} from '../../../api/chatPublicApi';
import {chatAppSync} from '../utils/chatAppSync';
import {Input} from './richContent/Input';
import {Placeholder} from './richContent/Placeholder';

const messageFormSchema = z.object({
  message: sendEventAsPublicUserBody.shape.content,
});

interface MessageFormValue {
  message: string;
}

interface MessageFormProps {
  conversationId: string;
  isTyping: boolean;
  sellerName: string;
  customerId: string;
  tenantId: string;
}

export function MessageForm(props: MessageFormProps) {
  const {conversationId, customerId, tenantId} = props;

  const [failed, setFailed] = useState(false);
  const [sendMessage, sendMessageQuery] = chatPublicApi.useCreatePublicEventMutation();

  const form = useForm<MessageFormValue>({
    resolver: zodResolver(messageFormSchema),
    mode: 'onChange',
    defaultValues: {
      message: '',
    },
  });

  const sendTypingEvent = useMemo(
    () =>
      throttle(
        (conversationId: string, customerUserId: string, tenantId: string) => {
          chatAppSync.graphql({
            query: sendEvent,
            variables: {
              conversationId,
              id: crypto.randomUUID(),
              tenantId,
              type: Type.TYPING,
              userId: customerUserId,
            },
          });
        },
        5_000,
        {trailing: false}
      ),
    []
  );

  const messageValue = useWatch({control: form.control, name: 'message'});

  useEffect(
    function sendTypingOnMessageChange() {
      if (!messageValue || !customerId || !tenantId) {
        return;
      }

      sendTypingEvent(conversationId, customerId, tenantId);
    },
    [sendTypingEvent, conversationId, customerId, tenantId, messageValue]
  );

  const handleSubmit = (data: MessageFormValue) => {
    if (data.message.trim() === '') {
      return;
    }

    return sendMessage({conversationId: props.conversationId, content: data.message})
      .unwrap()
      .then(() => {
        form.reset();
        setFailed(false);
      })
      .catch(() => {
        setFailed(true);
      });
  };

  return (
    <FormProvider {...form}>
      <VStack spacing={1}>
        <form onSubmit={form.handleSubmit(handleSubmit)}>
          <div
            css={css`
              display: flex;
              background-color: ${({theme}) => theme.colors.general.white};
              border-radius: ${({theme}) => theme.radii.large};
              padding-left: ${getCssSize(5)};
            `}
          >
            <RichContentInput
              name="message"
              placeholder={i18n.t('chat.message.placeholder')}
              ContentEditableComponent={<Input />}
              PlaceholderComponent={
                <Placeholder placeholder={i18n.t('chat.message.placeholder')} />
              }
            />
            <div
              css={css`
                padding: ${getCssSize(1)};
                display: flex;
                align-items: center;
                gap: ${getCssSize(3)};
              `}
            >
              <Show when={sendMessageQuery.isLoading}>
                <Spinner size="xSmall" />
              </Show>
              <button
                disabled={
                  sendMessageQuery.isLoading || messageValue.length === 0 || !form.formState.isValid
                }
                css={css`
                  padding: 12px;
                  border-radius: ${({theme}) => theme.radii.border_radius_full};
                  background-color: ${({theme}) => theme.colors.accent.base};
                  color: ${({theme}) => theme.colors.fill.white};
                  &:disabled {
                    opacity: 0.5;
                    cursor: not-allowed;
                  }
                `}
              >
                <Icon value="content/send" size={4} />
              </button>
            </div>
          </div>
        </form>
        <Show when={form.formState.errors.message && form.getFieldState('message').isDirty}>
          <Box paddingLeft={5}>
            <Text color="danger" size="xxSmall">
              {i18n.t(`general.errors.${form.formState.errors.message?.type}`)}
            </Text>
          </Box>
        </Show>
        <Show when={failed}>
          <Box paddingLeft={5}>
            <Text color="danger" size="xxSmall">
              {i18n.t('chat.message.failed')}
            </Text>
          </Box>
        </Show>
        <Show when={props.isTyping}>
          <Box paddingLeft={5}>
            <HStack spacing={1}>
              <Text color="secondary" size="xxSmall" alternative>
                {props.sellerName}
              </Text>
              <Text color="secondary" size="xxSmall">
                {i18n.t('chat.message.isTyping')}
              </Text>
            </HStack>
          </Box>
        </Show>
      </VStack>
    </FormProvider>
  );
}
